import { useState, useEffect } from 'react'
import { Spinner } from '../components/ui'
import { dataAPI } from '../services/api'
import {
  FacebookConnectPage,
  InstagramConnectPage,
  WhatsAppConnectPage,
  TelegramConnectPage,
  LinkedInConnectPage,
  GoogleConnectPage,
} from '../components/pages'

// Provider metadata mapping
const PROVIDER_METADATA = {
  whatsapp: { name: 'WhatsApp', icon: '💬', color: 'green' },
  facebook: { name: 'Facebook', icon: '📘', color: 'blue' },
  instagram: { name: 'Instagram', icon: '📷', color: 'pink' },
  telegram: { name: 'Telegram', icon: '✈️', color: 'cyan' },
  linkedin: { name: 'LinkedIn', icon: '💼', color: 'blue' },
  google: { name: 'Google', icon: '🔍', color: 'red' },
}

const SocialConnectPage = () => {
  const [providers, setProviders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeProvider, setActiveProvider] = useState(() => {
    return localStorage.getItem('active_social_network') || 'facebook'
  })

  // Map provider names to their components
  const providerComponents = {
    facebook: FacebookConnectPage,
    instagram: InstagramConnectPage,
    whatsapp: WhatsAppConnectPage,
    telegram: TelegramConnectPage,
    linkedin: LinkedInConnectPage,
    google: GoogleConnectPage,
  }

  // Fetch providers from API
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await dataAPI.getAll()
        
        if (response.data.success && response.data.data?.social_providers) {
          const providerIds = response.data.data.social_providers
          
          // Map provider IDs to full provider objects with metadata
          const mappedProviders = providerIds
            .map(id => ({
              id,
              ...(PROVIDER_METADATA[id] || { name: id.charAt(0).toUpperCase() + id.slice(1), icon: '🔗', color: 'gray' })
            }))
            .filter(Boolean)
          
          setProviders(mappedProviders)
          
          // Set default active provider if not set and providers exist
          const savedProvider = localStorage.getItem('active_social_network')
          if (!savedProvider && mappedProviders.length > 0) {
            const defaultProvider = mappedProviders[0].id
            setActiveProvider(defaultProvider)
            localStorage.setItem('active_social_network', defaultProvider)
          } else if (savedProvider && mappedProviders.find(p => p.id === savedProvider)) {
            setActiveProvider(savedProvider)
          } else if (mappedProviders.length > 0) {
            setActiveProvider(mappedProviders[0].id)
          }
        }
      } catch (err) {
        console.error('Failed to fetch social providers:', err)
        setError('Failed to load providers')
        // Fallback to default providers if API fails
        const fallbackProviders = Object.keys(PROVIDER_METADATA).map(id => ({
          id,
          ...PROVIDER_METADATA[id]
        }))
        setProviders(fallbackProviders)
        const savedProvider = localStorage.getItem('active_social_network')
        if (savedProvider && fallbackProviders.find(p => p.id === savedProvider)) {
          setActiveProvider(savedProvider)
        } else if (fallbackProviders.length > 0) {
          setActiveProvider(fallbackProviders[0].id)
        }
      } finally {
        setLoading(false)
      }
    }

    fetchProviders()
  }, [])

  const handleProviderChange = (providerId) => {
    setActiveProvider(providerId)
    localStorage.setItem('active_social_network', providerId)
  }

  // Get the component for the active provider
  const ProviderComponent = providerComponents[activeProvider] || FacebookConnectPage

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error && providers.length === 0) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-full">
      {/* Left Sidebar - Provider Tabs */}
      <div className="w-full lg:w-64 flex-shrink-0">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">Providers</h2>
            <p className="text-xs text-gray-500 mt-1">Select a provider to connect</p>
          </div>
          <nav className="p-2">
            {providers.map((provider) => (
              <button
                key={provider.id}
                onClick={() => handleProviderChange(provider.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors mb-1 ${
                  activeProvider === provider.id
                    ? 'bg-blue-50 text-blue-700 font-medium border border-blue-200'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="text-xl">{provider.icon}</span>
                <span className="text-sm flex-1 text-left">{provider.name}</span>
                {activeProvider === provider.id && (
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Right Side - Provider Page */}
      <div className="flex-1 min-w-0">
        <ProviderComponent />
      </div>
    </div>
  )
}

export default SocialConnectPage

